import {
  Box,
  Card,
  Flex,
  Grid,
  Text,
  ThickChevronRightIcon,
} from "@radix-ui/themes";
import { useState } from "react";
import { Dot } from "@/components/logo/Dot";

export const Projects = () => {
  return (
    <Flex
      direction={"column"}
      justify={"start"}
      align={"center"}
      gap={"6"}
      className={"mt-16 w-full md:max-w-[768px]"}
      style={
        {
          "--line-offset": "calc((100% - 80px) / 3 * 2 + 80px + 208px - 9px)",
        } as React.CSSProperties
      }
    >
      <Grid
        className={
          "relative w-full max-w-[768px] md:grid-cols-3 md:gap-x-10 md:items-start"
        }
      >
        <div
          className={
            "absolute top-[18px] left-2 md:left-auto md:right-[var(--line-offset)] w-[1px] h-[var(--line-height)] bg-[var(--sand-4)] z-0"
          }
          style={
            {
              "--line-height": "calc(100% + 32px)",
            } as React.CSSProperties
          }
        />

        <Flex
          justify={"start"}
          align={"center"}
          gap={"2"}
          className={"w-52 md:justify-self-end z-10"}
        >
          <Box className={"w-[18px] h-[36px]"}>
            <Dot color={"var(--lime-8)"} />
          </Box>
          <Text size={"3"} weight={"medium"} color={"gray"}>
            사이드 프로젝트
          </Text>
        </Flex>
      </Grid>
      <Flex justify={"center"} wrap={"wrap"} gap={"6"} className={"w-full"}>
        {projects.map((item, index) => (
          <ProjectItem
            key={index}
            {...item}
            isLast={index === projects.length - 1}
          />
        ))}
      </Flex>
    </Flex>
  );
};

type Project = {
  period: string;
  periodSum: string;
  team: string;
  position: string;
  details: { title: string; descriptions?: string[] }[];
};

const projects: Project[] = [
  {
    period: "2026.01 ~ 2026.01",
    periodSum: "(1개월)",
    team: "개인",
    position: "포트폴리오 사이트 제작",
    details: [
      {
        title: "심볼, 텍스트 로고 제작",
        descriptions: ["대표 색상 선정 및 브랜딩"],
      },
      {
        title: "Tailwind CSS & Radix UI 활용",
        descriptions: ["다크모드 대응", "반응형 대응 (288 ~ 768px / 768px ~)"],
      },
      {
        title: "GSAP을 활용하여 애니메이션 구현",
        descriptions: ["메인 페이지와 상세 페이지 간 전환 애니메이션 구현"],
      },
      {
        title: "Vercel 배포 및 유지보수",
      },
    ],
  },
  {
    period: "2025.07 ~ 2025.08",
    periodSum: "(1개월)",
    team: "개인",
    position: "보안 강화 홈캠 시스템 개발",
    details: [
      {
        title: "Android 단말 2대를 Host / Client로 분리한 홈캠 시스템 설계",
      },
      {
        title: "관리자 승인 기반 접근 제어로 무단 접속 차단",
      },
      {
        title:
          "Cloudflare Tunnel 기반 HTTPS 스트리밍 구성",
      },
      {
        title: "HMAC 기반 요청 서명 검증으로 API 위 · 변조 방지",
      },
    ],
  },
];

const ProjectItem = ({
  period,
  periodSum,
  team,
  position,
  details,
  isLast,
}: Project & { isLast?: boolean }) => {
  const [openDetails, setOpenDetails] = useState<boolean>(false);

  return (
    <Grid
      className={
        "relative w-full max-w-[768px] md:grid-cols-3 md:gap-x-10 md:items-start"
      }
    >
      <div
        className={
          "absolute top-[18px] left-2 md:left-auto md:right-[var(--line-offset)] w-[1px] h-[var(--line-height)] bg-[var(--sand-4)] z-0"
        }
        style={
          {
            "--line-height": isLast ? "calc(100% - 18px)" : "calc(100% + 32px)",
          } as React.CSSProperties
        }
      />

      <Flex
        justify={"start"}
        align={"center"}
        gap={"2"}
        className={"w-52 md:justify-self-end z-10"}
      >
        <Flex
          justify={"center"}
          align={"center"}
          className={"w-[18px] h-[36px]"}
        >
          <Box className={"scale-50"}>
            <Dot color={"var(--sand-6)"} />
          </Box>
        </Flex>
        <Flex className={"work-period-group"}>
          <Text size={"3"} color={"gray"}>
            {period}
          </Text>
          <Text size={"2"} color={"gray"}>
            {periodSum}
          </Text>
        </Flex>
      </Flex>
      <Flex direction={"column"} className={"pl-7 md:pl-0 md:col-span-2"}>
        <Flex direction={"column"} className={"mb-2"}>
          <Text size={"4"} style={{ fontWeight: 600 }}>
            {team}
          </Text>
          <Text size={"3"} color={"gray"}>
            {position}
          </Text>
        </Flex>
        <Flex direction={"column"} gap={"2"}>
          <Flex
            align={"center"}
            gap={"2"}
            className={"cursor-pointer w-fit"}
            style={{ color: "var(--lime-8)" }}
            onClick={() => setOpenDetails((curr) => !curr)}
          >
            <ThickChevronRightIcon
              className={"transition-all"}
              style={{
                transform: openDetails ? "rotate(90deg)" : "rotate(0deg)",
              }}
            />
            <Text size={"2"} weight={"medium"}>
              주요 업무 내용 {openDetails ? "가리기" : "보기"}
            </Text>
          </Flex>
          {openDetails && (
            <Card className={"bg-[var(--sand-2)]"}>
              <ol className={"work-detail-ol rt-r-size-3"}>
                {details.map((detail, index) => (
                  <li key={index} className={"rt-r-size-3"}>
                    <Text weight={"medium"}>{detail.title}</Text>
                    <ul className={"work-detail-ul rt-r-size-3"}>
                      {detail?.descriptions &&
                        detail.descriptions.map((description, index) => (
                          <li key={index} className={"rt-r-size-3"}>
                            <Text>{description}</Text>
                          </li>
                        ))}
                    </ul>
                  </li>
                ))}
              </ol>
            </Card>
          )}
        </Flex>
      </Flex>
    </Grid>
  );
};
